import DataTable from "@/components/Common/DataTable";
import MultiSelect from "@/components/Common/MultiSelect";
import CommonTab from "@/components/Common/Tab";
import { TabMenu } from "@/types/Shared/tab";
import { useLocation, useNavigate } from "react-router-dom";
import { columns, PROJECT_STATUS_OPTIONS } from "./columns";
import { useReadProjectGroupsAll } from "@/libs/react-query/hooks/useProjectQuery";
import { useIsAdmin } from "@/hooks/useIsFinancialManager";
import { DEFAULT_PAGINATION } from "@/consts/common";
import { useState } from "react";
import {
  format,
  fromUnixTime,
  isAfter,
  isBefore,
  startOfDay,
  startOfYear,
  subMonths,
  endOfDay,
} from "date-fns";
import CommonRadio from "@/components/Common/Radio";

export default function ProjectManagement() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const tabData: TabMenu[] = [
    {
      label: "프로젝트 관리",
      path: "/management/projects",
    },
    {
      label: "작업 관리",
      path: "/management/tasks",
    },
  ];

  const { data, isLoading } = useReadProjectGroupsAll({
    pagination: DEFAULT_PAGINATION,
  });

  // 필터: 생성 날짜 (프리셋 라디오 + 커스텀 범위) + 프로젝트 상태 (멀티셀렉)
  const CREATE_DATE_PRESETS = [
    { label: "전체 보기", value: "all" },
    { label: "올해", value: "this_year" },
    { label: "3개월 전", value: "3m" },
    { label: "6개월 전", value: "6m" },
    { label: "커스텀", value: "custom" },
  ];
  const [createDatePreset, setCreateDatePreset] = useState("all");
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");
  const STATUS_UNSET_OPTION = "미선택";
  const [statusFilter, setStatusFilter] = useState<string[]>([]);

  const getCreateDateRange = (): [string, string] => {
    const today = format(new Date(), "yyyy-MM-dd");
    switch (createDatePreset) {
      case "this_year":
        return [format(startOfYear(new Date()), "yyyy-MM-dd"), today];
      case "3m":
        return [format(subMonths(new Date(), 3), "yyyy-MM-dd"), today];
      case "6m":
        return [format(subMonths(new Date(), 6), "yyyy-MM-dd"), today];
      case "custom":
        return [customDateFrom, customDateTo];
      default:
        return ["", ""];
    }
  };
  const [createDateFrom, createDateTo] = getCreateDateRange();

  const filteredProjectGroups = data?.project_groups.filter((item) => {
    if (createDateFrom || createDateTo) {
      if (!item.create_date) {
        return false;
      }
      const createDate = fromUnixTime(item.create_date);
      if (
        createDateFrom &&
        isBefore(createDate, startOfDay(new Date(createDateFrom)))
      ) {
        return false;
      }
      if (createDateTo && isAfter(createDate, endOfDay(new Date(createDateTo)))) {
        return false;
      }
    }
    if (statusFilter.length > 0) {
      const isUnset = !item.status;
      const matched = isUnset
        ? statusFilter.includes(STATUS_UNSET_OPTION)
        : statusFilter.includes(item.status ?? "");
      if (!matched) {
        return false;
      }
    }
    return true;
  });

  const dataWithClickHandlers = filteredProjectGroups?.map((item) => ({
    ...item,
    onRowClick: () => navigate(`/management/projects/${item.project_group_id}`),
    disabled: !isAdmin && !item.is_participating,
  }));

  if (isLoading) {
    return null;
  }

  return (
    <div>
      <CommonTab variants="string" data={tabData} state={state} />
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3 text-sm text-gray-A">
          <span className="font-medium">생성 날짜</span>
          {CREATE_DATE_PRESETS.map((preset) => (
            <CommonRadio
              key={preset.value}
              label={preset.label}
              name="create-date-preset"
              defaultValue={preset.value}
              checked={createDatePreset === preset.value}
              onChange={setCreateDatePreset}
            />
          ))}
          {createDatePreset === "custom" && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customDateFrom}
                onChange={(e) => setCustomDateFrom(e.target.value)}
                className="h-9 rounded-md border border-gray-D bg-white px-2 text-sm"
              />
              <span>~</span>
              <input
                type="date"
                value={customDateTo}
                onChange={(e) => setCustomDateTo(e.target.value)}
                className="h-9 rounded-md border border-gray-D bg-white px-2 text-sm"
              />
            </div>
          )}
        </div>
        <MultiSelect
          className="w-[220px]"
          options={[
            STATUS_UNSET_OPTION,
            ...PROJECT_STATUS_OPTIONS.map((option) => option.value),
          ]}
          selected={statusFilter}
          onChange={setStatusFilter}
          placeholder="프로젝트 상태"
        />
        {(createDatePreset !== "all" || statusFilter.length > 0) && (
          <button
            type="button"
            className="text-sm text-gray-B underline"
            onClick={() => {
              setCreateDatePreset("all");
              setCustomDateFrom("");
              setCustomDateTo("");
              setStatusFilter([]);
            }}
          >
            필터 초기화
          </button>
        )}
      </div>
      <div className="mt-3 bg-white border border-gray-D rounded-xl">
        <DataTable
          columns={columns}
          data={dataWithClickHandlers || []}
          isPagination={true}
        />
      </div>
    </div>
  );
}
